import React, { useEffect, useState, useContext } from "react";
import { Container, Row, Col, Card, Button, Spinner, Modal, CloseButton } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaRegHeart,
  FaHeart,
  FaRegEye,
  FaShoppingCart,
  FaCartPlus,
  FaArrowUp,
} from "react-icons/fa";
import { productService } from "../services/productService";
import { FavoriteContext } from "../context/FavoriteContext";
import { CartContext } from "../context/CartContext";
import Swal from "sweetalert2";
import ProductDetailModal from "../components/ProductModalDetail";
import "../App.css";
import "../style/responsive.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchColors } from "../utils/colorUtils";


const ProductCard = ({
  title,
  mode = "grid",
  CARDS_PER_ROW = 4,
  slice,
  categories = [],
  categoriesLoading = false,
  collection = null,
  filterSex = null,
  layout = "grid",
  columns = 2 // NEW: Default to 2 columns for list layout
}) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { favorites, addFavorite, removeFavorite } = useContext(FavoriteContext);
  const { cart, addToCart, loadingItems } = useContext(CartContext);
  const [imageSearchResults] = useState([]);
  const [showImageSearchModal, setShowImageSearchModal] = useState(false);
  const navigate = useNavigate();
  const [loadingProductId, setLoadingProductId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [allCategories, setAllCategories] = useState(categories);
  const [isLoadingCategories, setIsLoadingCategories] = useState(categoriesLoading);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [currentImageIndices, setCurrentImageIndices] = useState({});
  const [availableColors, setAvailableColors] = useState([]);
  const [colorNameMap, setColorNameMap] = useState({});
  const [colorHexMap, setColorHexMap] = useState({});
  const [selectedBottleVariantIndex, setSelectedBottleVariantIndex] = useState({});


  // Replace your loadColors function with this:
  const loadColors = async () => {
    try {
      const allColors = await fetchColors();
      console.log("Fetched colors:", allColors);

      if (allColors && allColors.length > 0) {
        setAvailableColors(allColors);

        // Create maps for color names and hex codes
        const nameMap = {};
        const hexMap = {};
        allColors.forEach(color => {
          nameMap[color._id] = color.name;
          hexMap[color._id] = color.hexCode || "#CCCCCC";
        });
        setColorNameMap(nameMap);
        setColorHexMap(hexMap);
        console.log("Color name map:", nameMap);
        console.log("Color hex map:", hexMap);
      }
    } catch (error) {
      console.error("Error fetching colors:", error);
    }
  };

  // Get bottle variant data for a product
  const getBottleVariantData = (product) => {
    if (!product?.bottleVariants || product.bottleVariants.length === 0) {
      return null;
    }

    // Check if ANY bottle variant has valid data
    const hasValidData = product.bottleVariants.some(variant => {
      const hasBottleSize = variant.bottleSize && parseFloat(variant.bottleSize) > 0;
      const hasSellingPrice = variant.sellingPrice && parseFloat(variant.sellingPrice) > 0;
      const hasStock = variant.stock && parseFloat(variant.stock) > 0;
      const hasValidCapacityVariants = variant.capacityVariants &&
        variant.capacityVariants.some(c =>
          c.capacity && c.capacity.trim() !== '' &&
          c.price && parseFloat(c.price) > 0
        );
      return hasBottleSize || hasSellingPrice || hasStock || hasValidCapacityVariants;
    });

    // If no valid data, return null (treat as no bottle variants)
    if (!hasValidData) {
      return null;
    }

    const index = selectedBottleVariantIndex[product._id] || 0;
    const variant = product.bottleVariants[index];

    if (!variant) return null;

    // Calculate final price after discount
    const sellingPrice = parseFloat(variant.sellingPrice) || 0;
    const discountValue = parseFloat(variant.discountValue) || 0;
    let finalPrice = sellingPrice;
    let discountPercent = 0;

    if (discountValue > 0) {
      if (variant.discountType === 'percentage') {
        finalPrice = sellingPrice - (sellingPrice * discountValue / 100);
        discountPercent = discountValue;
      } else {
        // Fixed amount discount - calculate percentage
        finalPrice = sellingPrice - discountValue;
        if (sellingPrice > 0) {
          discountPercent = (discountValue / sellingPrice) * 100;
        }
      }
    }

    // IMPORTANT: Use main product stock if bottle variant has no stock
    // This ensures products with empty bottle variants still show as "In Stock"
    const effectiveStock = (variant.stock || 0) > 0
      ? variant.stock
      : (product.Item || 0);

    return {
      variant,
      index,
      finalPrice,
      sellingPrice,
      bottleSize: variant.bottleSize || 0,
      stock: effectiveStock, // Use effective stock
      totalVariants: product.bottleVariants.length,
      hasDiscount: discountValue > 0,
      discountValue: discountValue,
      discountPercent: discountPercent,
      discountType: variant.discountType,
      displayDiscount: discountValue > 0 ? (variant.discountType === 'percentage' ? discountValue : discountPercent) : 0,
      // Also track if this is a "virtual" stock (from main product)
      isVirtualStock: (variant.stock || 0) === 0 && (product.Item || 0) > 0
    };
  };

  // Add this function to process product colors (same as modal)
  const processProductColors = (product) => {
    if (!product?.ProductColor || !Array.isArray(product.ProductColor) || product.ProductColor.length === 0) {
      return [];
    }

    const productColors = product.ProductColor.map((colorId, index) => {
      const colorName = colorNameMap[colorId] || colorId;
      const colorHex = colorHexMap[colorId] || "#CCCCCC";
      const colorStock = product.colorStocks?.find(stock =>
        stock.color === colorId || stock.color === colorName
      );

      return {
        id: colorId,
        name: colorName,
        hexCode: colorHex,
        quantity: colorStock?.quantity ?? 0,
        index: index
      };
    }).filter(color => color);

    return productColors;
  };

  // Check if the user is logged in
  const isUserLoggedIn = () => {
    const user = localStorage.getItem("user");
    return !!user;
  };

  const ImageDotsIndicator = ({ images, currentIndex, onDotClick }) => {
    if (!images || images.length <= 1) return null;

    return (
      <div className="d-flex justify-content-center gap-2 position-absolute bottom-0 mb-3 w-100">
        {images.map((_, index) => (
          <button
            key={index}
            className={`p-0 border-0 bg-transparent`}
            onClick={(e) => {
              e.stopPropagation();
              onDotClick && onDotClick(index);
            }}
            aria-label={`Go to image ${index + 1}`}
          >
            <div
              className="rounded-circle"
              style={{
                width: '8px',
                height: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor:
                  index === currentIndex
                    ? '#3498db'
                    : 'rgba(5, 155, 226, 0.4)',
              }}
            />
          </button>
        ))}
      </div>
    );
  };

  const handleImageDotClick = (productId, index) => {
    setCurrentImageIndices(prev => ({
      ...prev,
      [productId]: index
    }));
  };

  const getCurrentImageIndex = (productId) => {
    return currentImageIndices[productId] || 0;
  };

  const getProductImages = (product) => {
    if (product.images && product.images.length > 0) {
      return product.images;
    } else if (product.image) {
      return [product.image];
    }
    return [];
  };

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;

          if (currentScrollY > 300) {
            // Detect scroll direction
            if (currentScrollY > lastScrollY) {
              // Scrolling DOWN - show button
              setShowScrollTop(true);
            } else if (currentScrollY < lastScrollY) {
              // Scrolling UP - hide button
              setShowScrollTop(false);
            }
          } else {
            // Near the top - always hide button
            setShowScrollTop(false);
          }

          setLastScrollY(currentScrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    if (categories.length > 0) {
      setAllCategories(categories);
      setIsLoadingCategories(false);
    }
  }, [categories]);

  useEffect(() => {
    const fetchProducts = async () => {
      const cacheKey = "cachedProducts";
      const cacheTimestampKey = "cachedProductsTimestamp";

      const cachedProducts = localStorage.getItem(cacheKey);

      if (cachedProducts) {
        // Show cached immediately
        const parsed = JSON.parse(cachedProducts);
        setProducts(parsed);
        setIsLoading(false);
      }

      try {
        // Always fetch latest in background
        const data = await productService.getAllProducts();

        if (data && data.length > 0) {
          setProducts(data);
          localStorage.setItem(cacheKey, JSON.stringify(data));
          localStorage.setItem(cacheTimestampKey, Date.now().toString());
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Call this when component mounts
  useEffect(() => {
    loadColors();
  }, []);

  // Filter products based on search query, category, collection, and sex
  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
    const matchesCollection = collection ? product.collection === collection : true;
    const matchesSex = filterSex ? product.sex === filterSex : true;
    return matchesSearch && matchesCategory && matchesCollection && matchesSex;
  });

  // Handle category change
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setCurrentIndex(0);
  };

  // Handle next button for row mode
  const handleNext = () => {
    if (currentIndex + 1 < filteredProducts.length) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
      setDirection(1);
    }
  };

  // Handle previous button for row mode
  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prevIndex) => prevIndex - 1);
      setDirection(-1);
    }
  };

  // Check if a product is already in the cart
  const isProductInCart = (productId) => {
    if (!cart || cart.length === 0 || !productId) return false;
    return cart.some((cartItem) => cartItem.product?._id === productId);
  };

  // Handle adding a product to the cart (used by both card and modal)
  const handleAddToCart = async (product) => {
    console.log("=== START handleAddToCart ===");
    console.log("Product:", product.title);

    if (!isUserLoggedIn()) {
      setShowLoginModal(true);
      return;
    }

    // Create a copy of the product to modify
    let productToAdd = { ...product };

    // Check if product has bottle variants with VALID data
    const hasValidBottleData = product.bottleVariants && product.bottleVariants.some(variant => {
      const hasBottleSize = variant.bottleSize && parseFloat(variant.bottleSize) > 0;
      const hasSellingPrice = variant.sellingPrice && parseFloat(variant.sellingPrice) > 0;
      const hasStock = variant.stock && parseFloat(variant.stock) > 0;
      const hasValidCapacityVariants = variant.capacityVariants &&
        variant.capacityVariants.some(c =>
          c.capacity && c.capacity.trim() !== '' &&
          c.price && parseFloat(c.price) > 0
        );
      return hasBottleSize || hasSellingPrice || hasStock || hasValidCapacityVariants;
    });

    // Check if product has capacity variants with VALID data
    const hasValidCapacityVariants = product.capacityVariants &&
      product.capacityVariants.length > 0 &&
      product.capacityVariants.some(v => v.price && parseFloat(v.price) > 0);

    // Only consider variants if they have valid data
    const hasBottleVariants = product.bottleVariants && product.bottleVariants.length > 0 && hasValidBottleData;
    const hasCapacityVariants = product.capacityVariants && product.capacityVariants.length > 0 && hasValidCapacityVariants;

    // If product has bottle variants with valid data AND we're NOT in the modal (no selectedCapacity or selectedBottleVariant)
    if (hasBottleVariants && !product.selectedCapacity && !product.selectedBottleVariant) {
      // Open the modal for selection
      handleShowModal(product);
      return;
    }

    // If product has capacity variants with valid data but no bottle variants
    if (hasCapacityVariants && !hasBottleVariants && !product.selectedCapacity) {
      // Try to find "Full Box" capacity
      let selectedCapacityVariant = product.capacityVariants.find(v =>
        v.name && v.name.toLowerCase().includes('full box')
      );

      // If no "Full Box", use the first available capacity variant
      if (!selectedCapacityVariant) {
        selectedCapacityVariant = product.capacityVariants[0];
      }

      if (selectedCapacityVariant) {
        productToAdd.price = selectedCapacityVariant.price || product.price;
        productToAdd.Item = selectedCapacityVariant.stock || product.Item;
        productToAdd.selectedCapacity = selectedCapacityVariant;
        productToAdd.discount = 0;
        productToAdd.displayTitle = `${product.title} (${selectedCapacityVariant.name})`;
        console.log("Selected capacity variant:", selectedCapacityVariant.name);
      }
    }

    // If product has bottle variants and we have selection info (from modal)
    if (hasBottleVariants && (product.selectedCapacity || product.selectedBottleVariant)) {
      // The product already has selection info from the modal
      // Just use it as-is
      productToAdd = { ...product };
    }

    // For regular products (no valid bottle variants, no valid capacity variants)
    if (!hasBottleVariants && !hasCapacityVariants) {
      productToAdd.discount = product.discount || 0;
      productToAdd.price = product.price || 0;
      productToAdd.displayTitle = product.title;
    }

    // Check if product has colors
    if (product.ProductColor && product.ProductColor.length > 0 && !product.selectedColor) {
      const productColors = processProductColors(product);
      if (productColors && productColors.length > 0) {
        let selectedColorData = productColors.find(color => (color.quantity || 0) > 0);
        if (!selectedColorData && productColors.length > 0) {
          selectedColorData = productColors[0];
        }
        if (selectedColorData) {
          productToAdd.selectedColor = {
            id: selectedColorData.id,
            name: selectedColorData.name,
            hexCode: selectedColorData.hexCode,
            quantity: selectedColorData.quantity
          };
          productToAdd.displayTitle = `${productToAdd.displayTitle || product.title} (${selectedColorData.name})`;
          const colorIndex = selectedColorData.index;
          if (product.images && product.images[colorIndex]) {
            productToAdd.image = product.images[colorIndex];
          }
          console.log("Selected color:", selectedColorData.name);
        }
      }
    }

    // Check if product is already in cart (checking ALL variants: capacity, bottle size, color)
    const isInCart = cart.some((cartItem) => {
      if (cartItem.product._id !== productToAdd._id) return false;

      // Check capacity match
      const itemCapacityId = cartItem.product.selectedCapacity?._id;
      const newCapacityId = productToAdd.selectedCapacity?._id;
      const capacityMatch = (!itemCapacityId && !newCapacityId) ||
        (itemCapacityId && newCapacityId && itemCapacityId === newCapacityId);

      // Check bottle size match
      const itemBottleSize = cartItem.product.selectedBottleVariant?.bottleSize;
      const newBottleSize = productToAdd.selectedBottleVariant?.bottleSize;
      const bottleMatch = (!itemBottleSize && !newBottleSize) ||
        (itemBottleSize && newBottleSize && itemBottleSize === newBottleSize);

      // Check color match
      const itemColorId = cartItem.product.selectedColor?.id;
      const newColorId = productToAdd.selectedColor?.id;
      const colorMatch = (!itemColorId && !newColorId) ||
        (itemColorId && newColorId && itemColorId === newColorId);

      return capacityMatch && bottleMatch && colorMatch;
    });

    console.log("Is in cart?", isInCart);
    console.log("Product to add:", productToAdd);

    if (isInCart) {
      Swal.fire({
        icon: "info",
        title: t("already_in_cart_title"),
        text: t("already_in_cart_text", { product: product.title }),
        showConfirmButton: true,
        confirmButtonText: t("go_to_cart"),
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/cart");
        }
      });
      return;
    }

    try {
      setLoadingProductId(productToAdd._id);
      await new Promise((resolve) => setTimeout(resolve, 800));
      const quantity = productToAdd.quantity || 1;
      await addToCart(productToAdd, quantity);
      console.log("addToCart completed successfully");
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setLoadingProductId(null);
    }
  };

  const handleFavoriteToggle = (product) => {
    if (favorites.some((fav) => fav._id === product._id)) {
      removeFavorite(product._id);
    } else {
      addFavorite(product);
    }
  };

  // Show product detail modal
  const handleShowModal = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  // Close product detail modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  const startSlice = slice ? slice[0] : 0;
  const endSlice = slice ? slice[1] : filteredProducts.length;
  const slicedProducts = filteredProducts.slice(startSlice, endSlice);
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 576;
  const isListLayout = layout === "list";

  // Replace the existing getColumnSizes function with this:
  const getColumnSizes = () => {
    if (isListLayout) {
      // For list layout: 2 columns on all screen sizes
      return {
        xs: 6,
        sm: 6,
        md: 6,
        lg: 6,
        xl: 6
      };
    }

    // For grid layout with CARDS_PER_ROW
    const cols = 12 / CARDS_PER_ROW;

    // Ensure we have at least 2 columns on tablet
    const tabletCols = Math.min(cols, 6); // Max 2 columns (6) on tablet

    return {
      xs: 6,     // Mobile: 2 columns (50%)
      sm: 6,     // Small: 2 columns (50%)
      md: 6,     // iPad/Tablet: 2 columns (50%) ← KEY CHANGE
      lg: cols,  // Desktop: use CARDS_PER_ROW
      xl: cols   // Large desktop: use CARDS_PER_ROW
    };
  };

  let displayedProducts;
  if (isListLayout) {
    // Show all products with 2-column layout
    displayedProducts = slicedProducts;
  } else if (mode === "grid" || isMobile) {
    displayedProducts = slicedProducts;
  } else {
    displayedProducts = slicedProducts.slice(currentIndex, currentIndex + CARDS_PER_ROW);
  }

  const isNextDisabled =
    mode === "row" && currentIndex + CARDS_PER_ROW >= slicedProducts.length;
  const isPreviousDisabled = mode === "row" && currentIndex === 0;

  const cardVariants = {
    initial: (direction) => ({
      opacity: 0,
      x: direction > 0 ? 100 : -100,
      scale: 0.9,
    }),
    animate: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 20,
      },
    },
    exit: (direction) => ({
      opacity: 0,
      x: direction > 0 ? -100 : 100,
      scale: 0.9,
      transition: {
        duration: 0.5,
      },
    }),
  };

  const colSizes = getColumnSizes();

  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <section className=".Shopping-page shopping-section" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
      <Container>

        <div className="title py-0 py-md-5 d-flex justify-content-center align-items-center flex-column">
          <div className="title d-flex align-items-center justify-content-center flex-column pb-md-5 mt-5">
            <h4 className="shop-search-title text-center">{title}</h4>
            <div className="title-line-style mt-2 mt-md-0"></div>
          </div>
          {mode === "grid" && (
            <div
              className="d-flex seach-component align-items-center position-relative mt-top"
              style={{
                width: '100%',
                maxWidth: '100%',
              }}
            >
              <input
                type="search"
                className="form-control product-search py-3 px-4"
                placeholder={t('Search Product')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: '100%'
                }}
              />
            </div>
          )}
        </div>

        {mode === "grid" && allCategories.length > 0 && (
          <div className="category-filter-section d-flex justify-content-center mt-4 mb-md-5 mt-md-0">
            <div
              className="d-flex gap-2 pb-2"
              style={{
                overflowX: "auto",
                whiteSpace: "nowrap",
                scrollbarWidth: "none",
              }}
            >
              {/* All Brands - No badge */}
              <button
                className={`btn btn-sm brand-button py-2 px-3 ${selectedCategory === "all"
                  ? "btn-color text-white"
                  : "btn-outline-secondary"
                  }`}
                onClick={() => handleCategoryChange({ target: { value: "all" } })}
              >
                {t("All Brands")}
              </button>

              {/* Categories with product count - SORTED by ACTUAL count with gender filter */}
              {allCategories
                .map((category) => {
                  // ✅ Count products in this category from products WITH gender filter applied
                  const productCount = products.filter((product) => {
                    // Must match category
                    const matchesCategory = product.category === category.name;
                    // Must match gender filter (if applied)
                    const matchesSex = filterSex ? product.sex === filterSex : true;
                    // Must match collection filter (if applied)
                    const matchesCollection = collection ? product.collection === collection : true;
                    // Must match search (if applied)
                    const matchesSearch = product.title.toLowerCase().includes(searchQuery.toLowerCase());

                    return matchesCategory && matchesSex && matchesCollection && matchesSearch;
                  }).length;

                  return {
                    ...category,
                    productCount,
                  };
                })
                .sort((a, b) => {
                  // Sort: products first (descending), then empty brands
                  if (a.productCount === 0 && b.productCount === 0) return 0;
                  if (a.productCount === 0) return 1;
                  if (b.productCount === 0) return -1;
                  return b.productCount - a.productCount;
                })
                .map((category) => {
                  const { productCount } = category;

                  return (
                    <button
                      key={category._id}
                      className={`btn btn-sm brand-button px-4 ${selectedCategory === category.name
                        ? "btn-color text-white"
                        : "btn-outline-secondary"
                        }`}
                      onClick={() =>
                        handleCategoryChange({ target: { value: category.name } })
                      }
                      style={{
                        position: "relative",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        opacity: productCount === 0 ? 0.5 : 1,
                      }}
                    >
                      {category.name}
                      {productCount > 0 ? (
                        <span
                          className="rounded-circle d-inline-flex align-items-center justify-content-center"
                          style={{
                            backgroundColor: selectedCategory === category.name
                              ? "rgba(255,255,255,0.25)"
                              : "rgba(0,0,0,0.08)",
                            color: selectedCategory === category.name
                              ? "#ffffff"
                              : "#666666",
                            fontSize: "10px",
                            width: "20px",
                            height: "20px",
                            fontWeight: 600,
                          }}
                        >
                          {productCount}
                        </span>
                      ) : (
                        <span
                          className="rounded-pill px-2"
                          style={{
                            backgroundColor: "rgba(200,200,200,0.2)",
                            color: "#999999",
                            fontSize: "9px",
                            padding: "1px 6px",
                            fontWeight: 400,
                          }}
                        >
                          {t("empty")}
                        </span>
                      )}
                    </button>
                  );
                })}
            </div>
          </div>
        )}

        <AnimatePresence>
          <Row className={`justify-content-start g-3 g-md-4 mt-2 ${isListLayout ? 'flex-row' : ''}`}>
            {displayedProducts.map((product) => {
              // Get bottle variant data for this product
              const bottleInfo = getBottleVariantData(product);
              const hasBottleVariants = product.bottleVariants && product.bottleVariants.length > 0;

              // Get price and stock from bottle variant if available
              let displayPrice = product.price;
              let displayDiscount = product.discount || 0;
              let displayStock = product.Item; // Default to main product stock
              let displayBottleSize = null;
              let totalVariants = 0;
              let originalPrice = product.price;
              let isVirtualStock = false;

              if (hasBottleVariants && bottleInfo) {
                // For bottle variants, use the variant's price and discount
                displayPrice = bottleInfo.finalPrice || bottleInfo.sellingPrice || product.price;
                // Use the effective stock (which falls back to main product stock if variant has no stock)
                displayStock = bottleInfo.stock;
                displayBottleSize = bottleInfo.bottleSize;
                totalVariants = bottleInfo.totalVariants;
                originalPrice = bottleInfo.sellingPrice || product.price;
                isVirtualStock = bottleInfo.isVirtualStock || false;

                // Use the calculated discount value
                if (bottleInfo.hasDiscount) {
                  displayDiscount = bottleInfo.discountType === 'percentage'
                    ? bottleInfo.discountValue
                    : bottleInfo.discountPercent;
                } else {
                  displayDiscount = 0;
                }
              } else {
                // For regular products (no bottle variants)
                // Use the product's own discount
                displayDiscount = product.discount || 0;
                displayPrice = product.price || 0;
                originalPrice = product.price || 0;
                displayStock = product.Item || 0; // Use main product stock

                // If there's a discount, calculate the discounted price
                if (displayDiscount > 0) {
                  displayPrice = product.price * (1 - displayDiscount / 100);
                }
              }

              return (
                <Col
                  xs={colSizes.xs}
                  sm={colSizes.sm}
                  md={colSizes.md}
                  lg={colSizes.lg}
                  xl={colSizes.xl}
                  key={product._id}
                  className="mb-custom-phone mb-0 mb-md-4 mt-3"
                >
                  <motion.div
                    custom={direction}
                    variants={cardVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    <Card className="border-0 p-0 m-1 position-relative product-screen-phone bg-transparent shadow-none">
                      <Card.Body className="border-0 py-3 px-0 d-flex flex-column justify-content-center">
                        <div className="d-flex position-relative hover-icon-show flex-column justify-content-center align-items-center">
                          <img
                            src={getProductImages(product)[getCurrentImageIndex(product._id)]}
                            alt={product.title}
                            className="img-fluid w-100 ProductCardSize"
                            style={{ objectFit: "contain", cursor: "pointer" }}
                            onClick={() => handleShowModal(product)}
                          />

                          <ImageDotsIndicator
                            images={getProductImages(product)}
                            currentIndex={getCurrentImageIndex(product._id)}
                            onDotClick={(index) => handleImageDotClick(product._id, index)}
                          />

                          {/* Display discount badge - show for both bottle variants and regular products */}
                          {displayDiscount > 0 && (
                            <span className="position-absolute px-2 ms-2 rounded-5 text-white btn-color-dis start-0 top-0 discount">
                              -{Math.round(displayDiscount)}%
                            </span>
                          )}

                          {(!displayStock || displayStock <= 0) && (
                            <div className="sold-out-overlay">
                              <span>{t("sold_out")}</span>
                            </div>
                          )}

                          <div className="position-absolute top-0 end-0 p-0 p-md-2 gap-2 icon-overlay d-flex flex-column me-2 me-md-0">
                            <div
                              className="bg-white shadow-lg d-flex justify-content-center align-items-center rounded-5 icon-style favorite-icon"
                              onClick={() => handleFavoriteToggle(product)}
                            >
                              {favorites.some(
                                (fav) => fav._id === product._id
                              ) ? (
                                <FaHeart className="shadow-lg fs-5 fa-icon" />
                              ) : (
                                <FaRegHeart className="text-dark shadow-lg fs-5 fa-icon" />
                              )}
                            </div>
                            <div
                              className="bg-white shadow-lg d-flex justify-content-center align-items-center rounded-5 icon-style view-icon"
                              onClick={() => handleShowModal(product)}
                            >
                              <FaRegEye className="text-dark shadow-lg fa-icon" />
                            </div>
                          </div>
                        </div>

                        <Card.Title className="mt-md-4 mt-3 mt-custom-phone border-0 ms-3">
                          <p
                            className="card-text-color m-0 p-0 description-limit product-title fs-md-4 fs-sm-6 text-center"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleShowModal(product)}
                          >
                            {product.title}
                          </p>
                        </Card.Title>

                        <Card.Title className="fw-medium fs-6 border-0 mx-0 my-md-1 p-0 description-color description-limit mb-2 mb-md-2 text-center">
                          <p
                            className="m-0 p-0 description-limit"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleShowModal(product)}
                          >
                            {product.description}
                          </p>
                        </Card.Title>

                        <Card.Title className="text-center fw-medium fs-5 my-md-2 border-0 price-color d-flex justify-content-center align-items-center gap-2">
                          {displayDiscount > 0 ? (
                            <>
                              <span className="text-decoration-line-through discount-price text-muted me-2">
                                ${(originalPrice || 0).toFixed(2)}
                              </span>
                              <span className="discount-price">
                                ${(displayPrice || 0).toFixed(2)}
                              </span>
                            </>
                          ) : (
                            <span className="discount-price">${(displayPrice || 0).toFixed(2)}</span>
                          )}
                        </Card.Title>
                      </Card.Body>
                      <Card.Footer className="p-0 border-0 add2 rounded-0">
                        <Card.Footer className="p-0 border-0 add2 rounded-0">
                          <Button
                            variant=""
                            className={`w-100 add-cart-button py-2 px-3 rounded-custom-phone border-0 text-white ${isProductInCart(product._id)
                              ? "bg-danger"
                              : "btn-color btn-card-hover"
                              }`}
                            onClick={() => {
                              // Check if product has valid options
                              const hasValidBottleData = product.bottleVariants && product.bottleVariants.some(variant => {
                                const hasBottleSize = variant.bottleSize && parseFloat(variant.bottleSize) > 0;
                                const hasSellingPrice = variant.sellingPrice && parseFloat(variant.sellingPrice) > 0;
                                const hasStock = variant.stock && parseFloat(variant.stock) > 0;
                                const hasValidCapacityVariants = variant.capacityVariants &&
                                  variant.capacityVariants.some(c =>
                                    c.capacity && c.capacity.trim() !== '' &&
                                    c.price && parseFloat(c.price) > 0
                                  );
                                return hasBottleSize || hasSellingPrice || hasStock || hasValidCapacityVariants;
                              });

                              const hasValidCapacityVariants = product.capacityVariants &&
                                product.capacityVariants.length > 0 &&
                                product.capacityVariants.some(v => v.price && parseFloat(v.price) > 0);

                              const hasValidVariants = (product.bottleVariants && product.bottleVariants.length > 0 && hasValidBottleData) ||
                                (product.capacityVariants && product.capacityVariants.length > 0 && hasValidCapacityVariants);

                              // If product has valid options, open modal for selection
                              // Otherwise, add directly to cart
                              if (hasValidVariants) {
                                handleShowModal(product);
                              } else {
                                handleAddToCart(product);
                              }
                            }}
                            disabled={!displayStock || displayStock <= 0 || loadingItems[product._id]}
                            style={
                              !displayStock || displayStock <= 0
                                ? { background: "#b0b0b0", cursor: "not-allowed", border: "none" }
                                : {}
                            }
                          >
                            {!displayStock || displayStock <= 0 ? (
                              t("sold_out")
                            ) : loadingItems[product._id] ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                {t("adding")}
                              </>
                            ) : (() => {
                              // Check if product has valid variants
                              const hasValidBottleData = product.bottleVariants && product.bottleVariants.some(variant => {
                                const hasBottleSize = variant.bottleSize && parseFloat(variant.bottleSize) > 0;
                                const hasSellingPrice = variant.sellingPrice && parseFloat(variant.sellingPrice) > 0;
                                const hasStock = variant.stock && parseFloat(variant.stock) > 0;
                                const hasValidCapacityVariants = variant.capacityVariants &&
                                  variant.capacityVariants.some(c =>
                                    c.capacity && c.capacity.trim() !== '' &&
                                    c.price && parseFloat(c.price) > 0
                                  );
                                return hasBottleSize || hasSellingPrice || hasStock || hasValidCapacityVariants;
                              });

                              const hasValidCapacityVariants = product.capacityVariants &&
                                product.capacityVariants.length > 0 &&
                                product.capacityVariants.some(v => v.price && parseFloat(v.price) > 0);

                              const hasValidVariants = (product.bottleVariants && product.bottleVariants.length > 0 && hasValidBottleData) ||
                                (product.capacityVariants && product.capacityVariants.length > 0 && hasValidCapacityVariants);

                              if (hasValidVariants) {
                                return (
                                  <>
                                    <FaCartPlus size={18} className="me-2" />
                                    {t("Select Options")}
                                  </>
                                );
                              } else if (isProductInCart(product._id)) {
                                return (
                                  <>
                                    <FaShoppingCart size={18} className="me-2" />
                                    {t("in_cart")}
                                  </>
                                );
                              } else {
                                return (
                                  <>
                                    <FaCartPlus size={18} className="me-2" />
                                    {t("add_to_cart")}
                                  </>
                                );
                              }
                            })()}
                          </Button>
                        </Card.Footer>
                      </Card.Footer>
                    </Card>
                  </motion.div>
                </Col>
              );
            })}

          </Row>
        </AnimatePresence>

        <Modal
          show={showImageSearchModal}
          onHide={() => setShowImageSearchModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Similar Products</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              {imageSearchResults.map((product) => (
                <Col key={product._id} md={4} className="mb-3">
                  <Card>
                    <Card.Img variant="top" src={product.image} />
                    <Card.Body>
                      <Card.Title>{product.title}</Card.Title>
                      <Card.Text>
                        Price: ${product.price.toFixed(2)}
                        {product.discount > 0 && (
                          <span className="text-success ms-2">
                            {product.discount}% OFF
                          </span>
                        )}
                      </Card.Text>
                      <Button
                        variant="primary"
                        onClick={() => handleAddToCart(product)}
                      >
                        Add to Cart
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Modal.Body>
        </Modal>
      </Container>

      {selectedProduct && (
        <ProductDetailModal
          show={showModal}
          onHide={handleCloseModal}
          product={selectedProduct}
          onAddToCart={handleAddToCart}
          isInCart={selectedProduct ? isProductInCart(selectedProduct._id) : false}
          loadingItems={loadingItems}
        />
      )}

      {/* Login Modal */}
      <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)}>
        <Modal.Body className="shadow-lg">
          <CloseButton
            className="close-btn p-0 float-end"
            onClick={() => setShowLoginModal(false)}
          />

          <div className="d-flex justify-content-between align-items-center mt-3">
            <p className="pt-3">Login is required!</p>
            <Button
              variant=""
              className="bg-transparent border px-3 login-hover"
              onClick={() => {
                setShowLoginModal(false);
                navigate("/login");
              }}
            >
              Log In
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="scroll-to-top-btn"
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            zIndex: 1000,
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            backgroundColor: '#dcd1bf',
            color: 'white',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
            opacity: 1,
            transform: 'translateY(0) scale(1)',
            animation: 'fadeInUp 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#dcd1bf';
            e.target.style.transform = 'translateY(0) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#dcd1bf';
            e.target.style.transform = 'translateY(0) scale(1)';
          }}
        >
          <FaArrowUp className="fs-5 text-dark" />
        </button>
      )}
    </section>
  );
};

export default ProductCard;

// Correct with 1089 line code changes
